import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

interface IdleContextType {
  isTabVisible: boolean;
  isMediaPlaying: boolean;
  isIdle: boolean; // True when tab hidden AND no media playing
  setMediaPlaying: (playing: boolean) => void;
}

const IdleContext = createContext<IdleContextType>({
  isTabVisible: true,
  isMediaPlaying: false,
  isIdle: false,
  setMediaPlaying: () => {}
});

export const useIdle = () => useContext(IdleContext);

interface IdleProviderProps {
  children: React.ReactNode;
}

export const IdleProvider: React.FC<IdleProviderProps> = ({ children }) => {
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
  const [isMediaPlaying, setIsMediaPlaying] = useState(false);
  const idleCallbacksRef = useRef<Set<() => void>>(new Set());
  const activeCallbacksRef = useRef<Set<() => void>>(new Set());

  // Calculate idle state
  const isIdle = !isTabVisible && !isMediaPlaying;

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsTabVisible(visible);

      if (visible) {
        // Tab became visible - notify active callbacks
        activeCallbacksRef.current.forEach(cb => cb());
      } else if (!isMediaPlaying) {
        // Tab hidden and no media - notify idle callbacks
        idleCallbacksRef.current.forEach(cb => cb());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMediaPlaying]);

  // Log state changes for debugging
  useEffect(() => {
    if (isIdle) {
      console.log('[IDLE] App entering idle mode - tab hidden, no media playing');
    } else {
      console.log('[IDLE] App active - visible:', isTabVisible, 'media:', isMediaPlaying);
    }
  }, [isIdle, isTabVisible, isMediaPlaying]);

  const setMediaPlaying = useCallback((playing: boolean) => {
    setIsMediaPlaying(playing);
  }, []);

  return (
    <IdleContext.Provider value={{
      isTabVisible,
      isMediaPlaying,
      isIdle,
      setMediaPlaying
    }}>
      {children}
    </IdleContext.Provider>
  );
};

/**
 * Hook to pause an interval when app is idle
 * Returns a function to clear the interval
 */
export const useIdleAwareInterval = (
  callback: () => void,
  delay: number | null,
  runWhenIdle: boolean = false
) => {
  const { isIdle } = useIdle();
  const savedCallback = useRef(callback);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    if (delay === null) return;

    // Don't run if idle (unless explicitly requested)
    if (isIdle && !runWhenIdle) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      savedCallback.current();
    }, delay);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [delay, isIdle, runWhenIdle]);

  return () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
};

/**
 * Hook to skip expensive computations when idle
 */
export const useIdleSkip = <T,>(
  compute: () => T,
  fallback: T,
  deps: React.DependencyList
): T => {
  const { isIdle } = useIdle();
  const lastValueRef = useRef<T>(fallback);

  // Only recompute when not idle
  useEffect(() => {
    if (!isIdle) {
      lastValueRef.current = compute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle, ...deps]);

  return isIdle ? lastValueRef.current : compute();
};

export default IdleContext;
